import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AdminLayout from 'cms/core/shared/AdminLayout';
import { UseCompanyDetail, UseJobList } from '../hooks';
import {
    Avatar,
    Box,
    Button,
    Chip,
    CircularProgress,
    Divider,
    Grid,
    IconButton,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Paper,
    Stack,
    Typography
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import LanguageIcon from '@mui/icons-material/Language';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import GroupsIcon from '@mui/icons-material/Groups';
import BusinessIcon from '@mui/icons-material/Business';
import AdminCompanyEditDialog from '../components/dialogs/AdminCompanyEditDialog';
import { JobHttpService } from '../service';
import { Utils } from 'core/utils';

const formatText = (value, fallback = '—') => {
    if (!Utils.normalizeText) return value ?? fallback;
    return Utils.normalizeText(value, fallback);
};

const getCountryLabel = (country) => {
    if (!country) return '';
    if (typeof country === 'object') return formatText(country.title ?? country.name ?? '');
    return formatText(country);
};

const SummaryCard = ({ icon, label, value }) => (
    <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider', height: '100%' }}>
        <Stack spacing={1}>
            <Stack direction="row" spacing={1} alignItems="center">
                {icon}
                <Typography variant="body2" color="text.secondary">
                    {label}
                </Typography>
            </Stack>
            <Typography variant="h4" fontWeight={700} color="text.primary">
                {value}
            </Typography>
        </Stack>
    </Paper>
);

const CompanyJobsCard = ({ jobs = [] }) => (
    <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider', height: '100%' }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
            <Typography variant="h6" fontWeight={600} color="text.primary">
                Open positions
            </Typography>
            <Chip label={`${jobs.length} active`} size="small" />
        </Stack>
        {jobs.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
                No active roles for this company.
            </Typography>
        ) : (
            <List disablePadding>
                {jobs.slice(0, 6).map((job) => (
                    <React.Fragment key={job.id}>
                        <ListItem disableGutters sx={{ py: 1.5 }}>
                            <ListItemAvatar>
                                <Avatar>{formatText(job.title).charAt(0)}</Avatar>
                            </ListItemAvatar>
                            <ListItemText
                                primary={formatText(job.title)}
                                secondary={job.location_description || job.address || '—'}
                            />
                            <Chip label={job.status ?? 'draft'} size="small" sx={{ textTransform: 'capitalize' }} />
                        </ListItem>
                        <Divider component="li" />
                    </React.Fragment>
                ))}
            </List>
        )}
    </Paper>
);

const CompanyDetailsPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [isEditDialogOpen, setEditDialogOpen] = useState(false);

    const { data: company, isLoading, refetch } = UseCompanyDetail(id);
    const companyJobFilters = useMemo(() => ({ company: id }), [id]);
    const { data: relatedJobsData } = UseJobList({ size: 50, filters: companyJobFilters });

    const relatedJobs = relatedJobsData?.results ?? [];

    const stats = useMemo(() => {
        if (!company) return [];
        return [
            {
                label: 'Open roles',
                value: relatedJobs.length,
                icon: <BusinessIcon color="primary" />
            },
            {
                label: 'Employees',
                value: company.number_of_employees ?? '—',
                icon: <GroupsIcon color="primary" />
            },
            {
                label: 'Rating',
                value: `${company.star ?? 3}/5`,
                icon: <Avatar sx={{ bgcolor: 'warning.light', color: 'warning.dark' }}>{company.star ?? 3}</Avatar>
            }
        ];
    }, [company, relatedJobs.length]);

    const handleDialogClose = () => setEditDialogOpen(false);

    const handleSaveCompany = async (payload) => {
        if (!company?.id) return;
        await JobHttpService.updateCompany(company.id, payload);
        await refetch();
    };

    if (isLoading) {
        return (
            <AdminLayout pageTitle="Company details">
                <Stack alignItems="center" justifyContent="center" minHeight="50vh">
                    <CircularProgress />
                </Stack>
            </AdminLayout>
        );
    }

    if (!company) {
        return (
            <AdminLayout pageTitle="Company details">
                <Paper sx={{ p: 4, borderRadius: 3 }}>
                    <Typography variant="h6">Company not found</Typography>
                    <Button sx={{ mt: 2 }} onClick={() => navigate(-1)}>
                        Back to companies
                    </Button>
                </Paper>
            </AdminLayout>
        );
    }

    return (
        <AdminLayout pageTitle={formatText(company.title)}>
            <Stack spacing={3}>
                <Stack direction="row" spacing={2} alignItems="center">
                    <IconButton onClick={() => navigate(-1)}>
                        <ArrowBackIcon />
                    </IconButton>
                    <Box>
                        <Typography variant="h4" fontWeight={700} color="text.primary">
                            {formatText(company.title)}
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            {formatText(company.industry?.title ?? 'General industry')}
                        </Typography>
                    </Box>
                    <Chip
                        icon={<LanguageIcon />}
                        label={company.website ? formatText(company.website) : 'No website'}
                        sx={{ ml: 2 }}
                    />
                    <Box sx={{ flexGrow: 1 }} />
                    <Button
                        variant="outlined"
                        startIcon={<EditOutlinedIcon />}
                        onClick={() => setEditDialogOpen(true)}
                    >
                        Edit company
                    </Button>
                </Stack>

                <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <Typography variant="h6" gutterBottom fontWeight={600} color="text.primary">
                                Overview
                            </Typography>
                            <Typography variant="body1" color="text.secondary">
                                {formatText(company.description || 'No description provided.')}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Stack spacing={2}>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <LocationOnIcon color="primary" />
                                    <Typography variant="body2" color="text.secondary">
                                        {[
                                            formatText(company.address, ''),
                                            formatText(company.city, ''),
                                            getCountryLabel(company.country)
                                        ]
                                            .filter(Boolean)
                                            .join(', ') || 'Location not set'}
                                    </Typography>
                                </Stack>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <PhoneIcon color="primary" />
                                    <Typography variant="body2" color="text.secondary">
                                        {formatText(company.phone_number || company.manager_name || 'No contact data')}
                                    </Typography>
                                </Stack>
                            </Stack>
                        </Grid>
                    </Grid>
                </Paper>

                <Grid container spacing={3}>
                    {stats.map((stat) => (
                        <Grid item xs={12} md={4} key={stat.label}>
                            <SummaryCard {...stat} />
                        </Grid>
                    ))}
                </Grid>

                <CompanyJobsCard jobs={relatedJobs} />
            </Stack>

            <AdminCompanyEditDialog
                open={isEditDialogOpen}
                onClose={handleDialogClose}
                company={company}
                onSave={handleSaveCompany}
            />
        </AdminLayout>
    );
};

export default CompanyDetailsPage;

